
import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ChevronDown,
  HelpCircle,
  Search,
  MessageCircle,
  Bell,
  ArrowRight
} from "lucide-react";
import UserHeader from "../components/UserHeader";
import InfoLayout from "./InfoLayout";

const faqs = [
  [
    "How do I request a car part?",
    "Open Find Parts, enter the available vehicle details and select your part. Follow the on-screen steps to submit a request. Include a part number or photo if you have one."
  ],
  [
    "Where can I see my quotes?",
    "Sign in and open My Quotes. Select a quote to review the details and the available actions."
  ],
  [
    "Can I ask a supplier a question?",
    "Yes. Use the Comments or conversation area associated with your request or quote to clarify fitment, condition or delivery."
  ],
  [
    "How do I check if a part will fit?",
    "Before approving a quote, confirm the vehicle year, make, model, engine and relevant part number with the supplier. Photos and VIN details may help identify a part."
  ],
  [
    "How are shipping, warranty and returns handled?",
    "These can differ by supplier and by part. Review the quote and confirm the relevant terms directly with the supplier before proceeding."
  ],
  [
    "I'm not receiving notifications. What should I do?",
    "Check Notifications in your account and refresh the page. If you still cannot see an expected update, return to the related conversation to check for messages."
  ]
];

export default function Support() {
  const [searchParams] = useSearchParams();
  const isAppView = searchParams.get("app") === "1";

  const appLink = (path) =>
    isAppView ? `${path}?app=1` : path;

  return (
    <>
      {!isAppView && <UserHeader />}

      <InfoLayout
        label="Support"
        title="How can we help?"
        subtitle="Find answers about requests, quotations, account access and supplier conversations."
      >
        <section
          className="fi-grid"
          aria-label="Common support topics"
        >
          <div className="fi-card">
            <Search size={27} />
            <h2>Finding parts</h2>
            <p>
              Need help starting a search or creating your
              first part request?
            </p>
            <Link
              className="fi-btn fi-btn-light"
              to={appLink("/search")}
            >
              Go to Find Parts <ArrowRight size={16} />
            </Link>
          </div>

          <div className="fi-card">
            <MessageCircle size={27} />
            <h2>Quotes & messages</h2>
            <p>
              Review your quotations and continue an
              existing supplier conversation.
            </p>
            <Link
              className="fi-btn fi-btn-light"
              to={appLink("/quotes")}
            >
              My Quotes <ArrowRight size={16} />
            </Link>
          </div>

          <div className="fi-card">
            <Bell size={27} />
            <h2>Account updates</h2>
            <p>
              Find the latest status changes and
              messages in your notifications.
            </p>
            <Link
              className="fi-btn fi-btn-light"
              to={appLink("/notifications")}
            >
              Notifications <ArrowRight size={16} />
            </Link>
          </div>
        </section>

        <section className="fi-section fi-split">
          <div>
            <h2 className="fi-section-title">
              Frequently asked questions
            </h2>

            <div className="fi-faq">
              {faqs.map(([q, a]) => (
                <details key={q}>
                  <summary>
                    {q}
                    <ChevronDown size={18} />
                  </summary>
                  <p>{a}</p>
                </details>
              ))}
            </div>
          </div>

          <div className="fi-panel-dark">
            <HelpCircle size={34} color="#fb4056" />

            <h2>Need more help?</h2>

            <p>
              For questions about a specific part or
              quotation, start with its supplier conversation
              so the details stay together.
            </p>

            <div className="fi-buttons">
              <Link
                className="fi-btn fi-btn-primary"
                to={appLink("/comments")}
              >
                Open Messages <ArrowRight size={17} />
              </Link>
            </div>

            <p
              className="fi-muted"
              style={{
                color: "#aebfd5",
                marginTop: 16
              }}
            >
              A dedicated support contact method can be
              added here when confirmed.
            </p>
          </div>
        </section>
      </InfoLayout>
    </>
  );
}
